import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import moment from "moment";
import {
  AccountBalanceWallet,
  Badge,
  Business,
  CalendarMonth,
  ErrorOutline,
  EventAvailable,
  Groups2,
  HourglassBottom,
  InfoOutlined,
  TrendingUp,
  WarningAmberRounded,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { parse, format } from "date-fns";
import { useHistory } from "react-router-dom";
import { customGetAPI } from "services/utils/CustomAPI";
import { setGlobalSearchValue } from "services/Redux/userToken";
import {
  setDepartmentId,
  setDepartmentName,
} from "services/Redux/userManagament";
import {
  defaultAllSelection,
  optionalAvathar,
  scrollTop,
} from "services/constants";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import sessionHandling from "services/utils/notificationUtils";
import {
  checkEditorPermission,
  checkViewerPermission,
} from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { FilterProvider } from "pages/FilterContext";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designTheme,
  designType,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { canonicalLeaveStatus } from "pages/TeamZone/Approve/Leave/LeaveStatusCards";
import { LeaveBalanceDetailPanel } from "./LeaveBalanceDetailPanel";
import { YearHeaderFilter } from "./YearHeaderFilter";

// "Employees Requiring Attention" and "Upcoming Leave" each preview only the
// first 4 employees inline; once a section has more than that, a "View all"
// action opens the full list in a side panel (ViewAllListPanel below) rather
// than growing the card or scrolling internally.
const ATTENTION_PREVIEW_COUNT = 4;

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) =>
  Number.isInteger(value) ? `${value}` : value.toFixed(1);

const sectionTitleSx = {
  fontSize: 15,
  fontWeight: 600,
  color: designColors.slate900,
};

const getBalanceChipSx = (value) => {
  if (value <= 0) {
    return {
      color: designColors.slate600,
      backgroundColor: designColors.slate100,
    };
  }
  if (value <= 3) {
    return {
      color: "#DC2626",
      backgroundColor: "#FEF2F2",
    };
  }
  if (value <= 7) {
    return {
      color: "#B45309",
      backgroundColor: "#FFF7ED",
    };
  }
  return {
    color: "#047857",
    backgroundColor: "#ECFDF5",
  };
};

// The info icon is only ever decorative if it has nothing to say — so it's
// only rendered once a `tooltip` message is passed in, wrapped in the same
// dark-styled Tooltip (tooltipStyleProps, services/constants/Styles) used
// for info icons elsewhere in the app (e.g. OffDayPostApproval.js's
// InfoRow), rather than a bare Mui.Tooltip with default light styling.
const SectionHeader = ({ title, tooltip, actionLabel, onAction }) => (
  <Mui.Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={1}
  >
    <Mui.Stack direction="row" spacing={0.85} alignItems="center">
      <Mui.Typography sx={sectionTitleSx}>{title}</Mui.Typography>
      {tooltip ? (
        <Mui.Tooltip title={tooltip} arrow slotProps={tooltipStyleProps}>
          <InfoOutlined
            sx={{ fontSize: 16, color: designColors.slate400, cursor: "pointer" }}
          />
        </Mui.Tooltip>
      ) : null}
    </Mui.Stack>
    {actionLabel ? (
      <Mui.Button
        onClick={onAction}
        sx={{
          minWidth: 0,
          p: 0,
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13.5,
        }}
      >
        {actionLabel}
      </Mui.Button>
    ) : null}
  </Mui.Stack>
);

// Shared "View all" destination for every capped preview list on this page
// (both Employees Requiring Attention sections, plus Upcoming Leave) — one
// side panel component so the three lists stay visually consistent instead
// of each growing its own drawer markup.
// Two render modes: a simple name/detail list (Low Leave Balance, Leave
// Expiring — panel.items) or a small table (Upcoming Leave — panel.columns +
// panel.rows), since Upcoming Leave needs several columns (leave type, date
// range, days) that don't fit the two-column list layout.
const ViewAllListPanel = ({ panel, expanded, onToggleExpand, onClose }) => (
  <GlobalSidePanel
    open={Boolean(panel)}
    onClose={onClose}
    expanded={expanded}
    onToggleExpand={onToggleExpand}
    title={panel?.title}
    subtitle={panel?.subtitle}
  >
    {panel?.columns ? (
      <Mui.TableContainer sx={{ p: { xs: 1.5, sm: 2.5 }, overflowX: "auto" }}>
        <Mui.Table size="small" sx={{ minWidth: 520 }}>
          <Mui.TableHead>
            <Mui.TableRow>
              {panel.columns.map((column) => (
                <Mui.TableCell
                  key={column.key}
                  sx={{ ...designShell.tableHeadCellBase, whiteSpace: "nowrap" }}
                >
                  {column.label}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {panel.rows?.length ? (
              panel.rows.map((row, index) => (
                <Mui.TableRow key={row.id ?? index} sx={designShell.tableRowHover}>
                  {panel.columns.map((column) => (
                    <Mui.TableCell
                      key={column.key}
                      sx={{
                        py: 1,
                        fontSize: 13,
                        color: designColors.slate800,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row[column.key]}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))
            ) : (
              <Mui.TableRow>
                <Mui.TableCell
                  colSpan={panel.columns.length}
                  sx={{ fontSize: 13, color: designColors.slate500 }}
                >
                  None right now.
                </Mui.TableCell>
              </Mui.TableRow>
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>
    ) : (
      <Mui.Stack spacing={1} sx={{ p: 2.5 }}>
        {panel?.items?.length ? (
          panel.items.map((item, index) => (
            <Mui.Stack
              key={`${item.name}-${index}`}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
              sx={{
                p: 1.25,
                borderRadius: designRadii.card,
                backgroundColor: designColors.slate100,
              }}
            >
              <Mui.Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                {item.avatarUrl !== undefined ? (
                  <Mui.Avatar
                    src={item.avatarUrl ?? optionalAvathar}
                    alt={item.name?.charAt(0)?.toUpperCase()}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : null}
                <Mui.Box sx={{ minWidth: 0 }}>
                  <Mui.Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: designColors.slate900,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.name}
                  </Mui.Typography>
                  {item.subtitle ? (
                    <Mui.Typography
                      sx={{ fontSize: 12, color: designColors.slate600 }}
                    >
                      {item.subtitle}
                    </Mui.Typography>
                  ) : null}
                </Mui.Box>
              </Mui.Stack>
              <Mui.Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: panel?.accent || designColors.slate700,
                  flexShrink: 0,
                }}
              >
                {item.detail}
              </Mui.Typography>
            </Mui.Stack>
          ))
        ) : (
          <Mui.Typography sx={{ fontSize: 13, color: designColors.slate500 }}>
            None right now.
          </Mui.Typography>
        )}
      </Mui.Stack>
    )}
  </GlobalSidePanel>
);

const LeaveSummarySkeleton = () => (
  <Mui.Stack spacing={2.5}>
    <Mui.Stack
      direction={{ xs: "column", lg: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", lg: "center" }}
      spacing={2}
    >
      <Mui.Stack direction="row" spacing={1.5} alignItems="center">
        <Mui.Skeleton
          variant="rounded"
          width={50}
          height={50}
          sx={{ borderRadius: 2 }}
        />
        <Mui.Box>
          <Mui.Skeleton variant="text" width={170} height={32} />
          <Mui.Skeleton variant="text" width={360} height={22} />
        </Mui.Box>
      </Mui.Stack>
      <Mui.Skeleton
        variant="rounded"
        width={250}
        height={40}
        sx={{ borderRadius: 2 }}
      />
    </Mui.Stack>

    <Mui.Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 2,
      }}
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <Mui.Paper
          key={index}
          variant="outlined"
          sx={{ p: 1.75, borderRadius: designRadii.card }}
        >
          <Mui.Skeleton variant="text" width="48%" height={20} />
          <Mui.Skeleton variant="text" width="64%" height={34} />
          <Mui.Skeleton variant="text" width="38%" height={18} />
        </Mui.Paper>
      ))}
    </Mui.Box>

    <Mui.Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" },
      }}
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <Mui.Paper
          key={index}
          variant="outlined"
          sx={{ p: 2.25, minHeight: 315, borderRadius: designRadii.card }}
        >
          <Mui.Skeleton variant="text" width="58%" height={24} />
          <Mui.Skeleton
            variant="rounded"
            width={170}
            height={170}
            sx={{ borderRadius: "50%", mx: "auto", my: 2 }}
          />
          {Array.from({ length: 3 }).map((__, rowIndex) => (
            <Mui.Stack
              key={rowIndex}
              direction="row"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Mui.Skeleton variant="text" width="42%" height={20} />
              <Mui.Skeleton variant="text" width="18%" height={20} />
            </Mui.Stack>
          ))}
        </Mui.Paper>
      ))}
    </Mui.Box>

    <Mui.Paper variant="outlined" sx={{ p: 2, borderRadius: designRadii.card }}>
      <Mui.Skeleton variant="text" width={220} height={26} />
      <Mui.Skeleton
        variant="rounded"
        height={44}
        sx={{ mt: 1.5, borderRadius: 1.5 }}
      />
      {Array.from({ length: 6 }).map((_, index) => (
        <Mui.Stack key={index} direction="row" spacing={2} sx={{ mt: 1.25 }}>
          <Mui.Skeleton variant="text" width="16%" height={22} />
          <Mui.Skeleton variant="text" width="18%" height={22} />
          <Mui.Skeleton variant="text" width="14%" height={22} />
          <Mui.Skeleton variant="text" width="12%" height={22} />
          <Mui.Skeleton variant="text" width="12%" height={22} />
        </Mui.Stack>
      ))}
    </Mui.Paper>
  </Mui.Stack>
);

// Same "Default View" sentinel + curated-subset pattern as Approve/Leave's
// list (TeamZone/Approve/Leave/index.js) — the default view shows every
// column, since that's what this table always showed before it gained a
// saved-view selector.
const LEAVE_SUMMARY_DEFAULT_VIEW_ID = "__default_leave_summary_view__";
const LEAVE_SUMMARY_HEADERS = [
  {
    key: "user_name",
    name: "Employee",
    type: "string",
    icon: <Groups2 fontSize="small" />,
  },
  {
    key: "employee_id",
    name: "Employee ID",
    type: "string",
    icon: <Badge fontSize="small" />,
  },
  {
    key: "department",
    name: "Department",
    type: "autocomplete",
    icon: <Business fontSize="small" />,
  },
  {
    key: "annual_allowance",
    name: "Annual Allowance",
    type: "number",
    icon: <EventAvailable fontSize="small" />,
  },
  {
    key: "credited_leave",
    name: "Credited",
    type: "number",
    icon: <TrendingUp fontSize="small" />,
  },
  {
    key: "planned_leave",
    name: "Planned",
    type: "number",
    icon: <CalendarMonth fontSize="small" />,
  },
  {
    key: "used_leave",
    name: "Used",
    type: "number",
    icon: <HourglassBottom fontSize="small" />,
  },
  {
    key: "expired_leave",
    name: "Expired",
    type: "number",
    icon: <ErrorOutline fontSize="small" />,
  },
  {
    key: "available_leave",
    name: "Balance",
    type: "number",
    icon: <AccountBalanceWallet fontSize="small" />,
  },
];
const LEAVE_SUMMARY_DEFAULT_HEADER_KEYS = LEAVE_SUMMARY_HEADERS.map(
  (header) => header.key,
);

// Same lookup EOD Report uses to resolve its own subModuleId
// (MyZone/EODReport/index.js) — a handful of name variants in case the
// backend's screen-access record spells this submodule differently.
const resolveLeaveSummarySubModuleId = (userScreenAccess) => {
  const modules = userScreenAccess?.module_details || [];

  for (const module of modules) {
    const match = module?.submodules?.find((submodule) =>
      ["Leave Summary", "Leavesummary", "Leave Balances"].includes(
        submodule?.sub_module_name,
      ),
    );
    if (match?.sub_module_id) return match.sub_module_id;
  }

  return undefined;
};

export const LeaveSummary = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { departmentId } = useSelector((state) => state.userReducer);
  const {
    domain,
    token,
    userDetails,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const { downloadCsv, downloadExcel, downloadPdf } = useExcelDownload();

  const [showDetails, setShowDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [leaveFilterData, setLeaveFilterData] = useState([]);
  // Raw individual leave-request rows (same bulk endpoint Approve/Leave
  // uses) — fetched alongside the aggregate summary purely so "Upcoming
  // Leave" can show each request's actual date range, which the aggregate
  // rows above don't carry (they only total up planned days per employee).
  const [upcomingLeaveRequests, setUpcomingLeaveRequests] = useState([]);
  const [leaveDate, setLeaveDate] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredLabel, setFilteredLabel] = useState("");
  const [filteredValue, setFilteredValue] = useState("");
  const [updateData, setUpdateData] = useState(true);
  const containerRef = useRef(null);
  const {
    expanded: leaveDetailPanelExpanded,
    toggleExpanded: toggleLeaveDetailPanelExpanded,
  } = useExpandablePanel(showDetails);
  // "View all" side panel shared by the two attention sections and Upcoming
  // Leave (ViewAllListPanel) — null when closed, otherwise
  // { title, subtitle, accent, items }.
  const [viewAllPanel, setViewAllPanel] = useState(null);
  const {
    expanded: viewAllPanelExpanded,
    toggleExpanded: toggleViewAllPanelExpanded,
  } = useExpandablePanel(Boolean(viewAllPanel));
  // Sticky page-level table chrome, same measurement pattern as Approve/Leave
  // (TeamZone/Approve/Leave/index.js): the CustomFilter bar's own height
  // varies once a filter/sort chip row appears, so the table head's sticky
  // offset (and the footer's reserved bottom padding) need the real
  // measured heights, not a guess.
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  const subModuleId = useMemo(
    () => resolveLeaveSummarySubModuleId(userScreenAccess),
    [userScreenAccess],
  );
  const check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Leave Balances",
    "Leave Balances",
  );
  const viewer_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Leave Balances",
    "Leave Balances",
  );

  useEffect(() => {
    dispatch(setDepartmentName("All"));
    dispatch(setDepartmentId(defaultAllSelection));
  }, [dispatch]);

  const formatDate = (dateString) => {
    const date = parse(dateString, "dd-MM-yyyy", new Date());
    return format(date, "yyyy-MM-dd");
  };

  useEffect(() => {
    if (topbarCompanyId) {
      setUpdateData(true);
    }
  }, [topbarCompanyId]);

  useEffect(() => {
    const fetchLeaveDate = async () => {
      try {
        const response = await fetch(`${domain}leaves-years/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const results = res.results || [];
          const nextLeaveDate = results.map((value) => ({
            label: value.name,
            start_date: value.start_date,
            end_date: value.end_date,
            filteredValue: `${value.start_date} - ${value.end_date}`,
          }));
          if (results[0]) {
            setLeaveDate(nextLeaveDate);
            setFilteredLabel(results[0].name);
            setFilteredValue(
              `${results[0].start_date} - ${results[0].end_date}`,
            );
            setStartDate(formatDate(results[0].start_date));
            setEndDate(formatDate(results[0].end_date));
          }
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[LeaveSummary] leave year load failed:", error);
        showToast(
          "error",
          "Couldn't load leave periods. Please refresh the page.",
        );
      }
    };

    fetchLeaveDate();
  }, [domain, token]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [res, requestsRes] = await Promise.all([
        customGetAPI(
          `${domain}leave-summary-admin/?department_id=${departmentId}&user_id=${userDetails?.id}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
        ),
        customGetAPI(
          `${domain}leave-request/?user_id=${userDetails?.id}&department_id=${departmentId}&selected_user_id=${defaultAllSelection}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
        ),
      ]);
      const rows = res.ok ? res?.responseDetails || [] : [];
      setMasterDataForSearch(rows);
      // Also seed the filtered view directly — CustomFilter recomputes
      // leaveFilterData in its own effect a render later, so without this
      // the table briefly shows the previous fetch's rows (or the "no
      // records" message on first load) right as loading ends.
      setLeaveFilterData(rows);
      setUpcomingLeaveRequests(
        requestsRes.ok ? requestsRes?.responseDetails || [] : [],
      );
      setLoading(false);
    };

    if (startDate && endDate && updateData) {
      fetchData();
      setUpdateData(false);
    }
  }, [
    departmentId,
    domain,
    endDate,
    startDate,
    topbarCompanyId,
    updateData,
    userDetails?.id,
  ]);

  const getTeammateDetails = async (
    nextStartDate,
    nextEndDate,
    nextDepartmentId,
  ) => {
    setLoading(true);
    const [res, requestsRes] = await Promise.all([
      customGetAPI(
        `${domain}leave-summary-admin/?department_id=${nextDepartmentId}&user_id=${userDetails?.id}&start_date=${nextStartDate}&end_date=${nextEndDate}&company_id=${topbarCompanyId}`,
      ),
      customGetAPI(
        `${domain}leave-request/?user_id=${userDetails?.id}&department_id=${nextDepartmentId}&selected_user_id=${defaultAllSelection}&start_date=${nextStartDate}&end_date=${nextEndDate}&company_id=${topbarCompanyId}`,
      ),
    ]);
    const rows = res.ok ? res?.responseDetails || [] : [];
    setMasterDataForSearch(rows);
    // Also seed the filtered view directly — see fetchData's comment above.
    setLeaveFilterData(rows);
    setUpcomingLeaveRequests(
      requestsRes.ok ? requestsRes?.responseDetails || [] : [],
    );
    if (res.ok) {
      setShowDetails(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const paginatedRows = useMemo(
    () =>
      leaveFilterData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [leaveFilterData, page, rowsPerPage],
  );

  // Stat cards / distribution / attention panels are computed from the full
  // department+year fetch (masterDataForSearch), not the table's own search
  // result (leaveFilterData) — same reasoning as Approve/Leave's status
  // cards: these should stay stable while someone searches the table below,
  // not flicker as they type.
  const summary = useMemo(() => {
    const rows = masterDataForSearch || [];
    const totalEmployees = rows.length;
    const totalAvailable = rows.reduce(
      (sum, item) => sum + safeNumber(item?.available_leave),
      0,
    );
    const totalExpired = rows.reduce(
      (sum, item) => sum + safeNumber(item?.expired_leave),
      0,
    );
    const lowBalanceEmployees = rows.filter(
      (item) => safeNumber(item?.available_leave) <= 3,
    ).length;

    const distribution = [
      {
        label: "More than 15 days",
        count: rows.filter((item) => safeNumber(item?.available_leave) > 15)
          .length,
        color: "#16A34A",
      },
      {
        label: "8 - 15 days",
        count: rows.filter((item) => {
          const value = safeNumber(item?.available_leave);
          return value >= 8 && value <= 15;
        }).length,
        color: "#2563EB",
      },
      {
        label: "3 - 7 days",
        count: rows.filter((item) => {
          const value = safeNumber(item?.available_leave);
          return value > 3 && value < 8;
        }).length,
        color: "#F97316",
      },
      {
        label: "Less than 3 days",
        count: rows.filter((item) => {
          const value = safeNumber(item?.available_leave);
          return value > 0 && value <= 3;
        }).length,
        color: "#EF4444",
      },
      {
        label: "No Balance",
        count: rows.filter((item) => safeNumber(item?.available_leave) <= 0)
          .length,
        color: designColors.slate300,
      },
    ];

    const totalDistribution =
      distribution.reduce((sum, item) => sum + item.count, 0) || 1;
    const distributionStops = distribution.reduce(
      (acc, item) => {
        const next = acc.offset + (item.count / totalDistribution) * 100;
        acc.parts.push(`${item.color} ${acc.offset}% ${next}%`);
        acc.offset = next;
        return acc;
      },
      { parts: [], offset: 0 },
    );

    return {
      totalEmployees,
      totalAvailable,
      totalExpired,
      averageBalance: totalEmployees ? totalAvailable / totalEmployees : 0,
      lowBalanceEmployees,
      distribution,
      distributionBackground: `conic-gradient(${distributionStops.parts.join(", ")})`,
    };
  }, [masterDataForSearch]);

  // Calculated (not raw) "needs action" call-outs, replacing the earlier
  // plain Annual Allowance/Credited/Planned/Used totals panel — those were
  // just aggregate sums with nothing an admin could act on; these are
  // specific employees to follow up with.
  const attentionSections = useMemo(() => {
    const rows = masterDataForSearch || [];
    const lowBalance = [...rows]
      .filter((item) => safeNumber(item?.available_leave) <= 3)
      .sort(
        (a, b) =>
          safeNumber(a?.available_leave) - safeNumber(b?.available_leave),
      );
    const expiring = [...rows]
      .filter((item) => safeNumber(item?.expired_leave) > 0)
      .sort(
        (a, b) => safeNumber(b?.expired_leave) - safeNumber(a?.expired_leave),
      );

    return [
      {
        key: "low-balance",
        title: "Low Leave Balance (≤ 3 Days)",
        count: lowBalance.length,
        accent: "#EF4444",
        tint: "#FEF2F2",
        icon: WarningAmberRounded,
        items: lowBalance.map((item) => ({
          name: item?.user_name || "N/A",
          detail: `${formatCount(safeNumber(item?.available_leave))} day${
            safeNumber(item?.available_leave) === 1 ? "" : "s"
          } left`,
        })),
      },
      {
        key: "expiring",
        title: "Leave Expiring This Period",
        count: expiring.length,
        accent: "#F97316",
        tint: "#FFF7ED",
        icon: HourglassBottom,
        items: expiring.map((item) => ({
          name: item?.user_name || "N/A",
          detail: `${formatCount(safeNumber(item?.expired_leave))} days expiring`,
        })),
      },
    ];
  }, [masterDataForSearch]);

  // "Upcoming Leave" sources from individual leave-request rows
  // (upcomingLeaveRequests, fetched alongside the aggregate summary above),
  // not masterDataForSearch — the aggregate rows only carry a total planned
  // days count with no date on them. Scoped to Approved requests starting
  // today or later (matches what "Planned" means elsewhere on this page:
  // already-approved future leave) and kept one row per request, since an
  // employee can have more than one upcoming approved leave in the period.
  const upcomingLeave = useMemo(() => {
    const startOfToday = moment().startOf("day");
    const all = (upcomingLeaveRequests || [])
      .filter((item) => canonicalLeaveStatus(item?.status) === "Approved")
      .filter((item) => {
        const from = moment(item?.from_date, "DD-MM-YYYY");
        return from.isValid() && !from.isBefore(startOfToday, "day");
      })
      .sort(
        (a, b) =>
          moment(a?.from_date, "DD-MM-YYYY").valueOf() -
          moment(b?.from_date, "DD-MM-YYYY").valueOf(),
      )
      .map((item) => {
        // Same "dd MMM yyyy" display format as the Approve > Leave detail
        // panel's leave-period line (LeaveDetailPanel.js's
        // formatDisplayDate) — falls back to the raw string if it doesn't
        // parse, rather than showing a blank date.
        const from = moment(item?.from_date, "DD-MM-YYYY");
        const to = moment(item?.to_date, "DD-MM-YYYY");
        return {
          name:
            item?.user_name || item?.username || item?.employee_name || "N/A",
          leaveType: item?.leave_type || item?.name || "Leave",
          fromDate: from.isValid() ? from.format("DD MMM YYYY") : item?.from_date,
          toDate: to.isValid() ? to.format("DD MMM YYYY") : item?.to_date,
          days: safeNumber(item?.business_days),
          imageUrl: item?.image_url,
        };
      });

    return { preview: all.slice(0, ATTENTION_PREVIEW_COUNT), all };
  }, [upcomingLeaveRequests]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Leave Year": filteredLabel || "N/A",
      "Total Employees": summary.totalEmployees,
      "Average Balance": `${formatCount(summary.averageBalance)} Days`,
      "Total Available": `${formatCount(summary.totalAvailable)} Days`,
      "Expiring This Period": `${formatCount(summary.totalExpired)} Days`,
      "Low Balance": summary.lowBalanceEmployees,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [filteredLabel, summary, topbarCompanyName],
  );

  const handleVisibilityToggle = (user) => {
    setSelectedUser(user);
    setShowDetails(true);
  };

  const handleClose = () => {
    setShowDetails(false);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filtered) => {
      const nextData = filtered || [];
      setLeaveFilterData(nextData);
      if (page * rowsPerPage >= nextData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const handleCloseViewAllPanel = useCallback(() => setViewAllPanel(null), []);

  // "Employees Requiring Attention" only ever previews the first 4 employees
  // per section inline (attentionSections still carries every match) — this
  // opens the full list for whichever section's "View all" was clicked.
  const handleViewAllAttention = useCallback((section) => {
    setViewAllPanel({
      title: section.title,
      subtitle: `${section.count} employee${section.count === 1 ? "" : "s"}`,
      accent: section.accent,
      items: section.items,
    });
  }, []);

  // "Upcoming Leave" only ever previews the top 4 (soonest first) out of
  // upcomingLeave.all — "View all" opens the full list as a small table so
  // each request's leave type, date range and day count all fit, rather
  // than duplicating it as a second table on the page.
  const handleViewAllUpcoming = useCallback(() => {
    setViewAllPanel({
      title: "Upcoming Leave",
      subtitle: `${upcomingLeave.all.length} upcoming leave${
        upcomingLeave.all.length === 1 ? "" : "s"
      }`,
      columns: [
        { key: "name", label: "Employee" },
        { key: "leaveType", label: "Leave Type" },
        { key: "dates", label: "Dates" },
        { key: "days", label: "Days" },
      ],
      rows: upcomingLeave.all.map((item, index) => ({
        id: `${item.name}-${item.fromDate}-${index}`,
        name: item.name,
        leaveType: item.leaveType,
        dates:
          item.fromDate === item.toDate
            ? item.fromDate
            : `${item.fromDate} – ${item.toDate}`,
        days: `${formatCount(item.days)} day${item.days === 1 ? "" : "s"}`,
      })),
    });
  }, [upcomingLeave.all]);

  // Default View / saved-view selector — same shape as Approve/Leave's list
  // (TeamZone/Approve/Leave/index.js).
  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_leave_summary_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: LEAVE_SUMMARY_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || LEAVE_SUMMARY_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== LEAVE_SUMMARY_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(() => {
    const keys = new Set(LEAVE_SUMMARY_DEFAULT_HEADER_KEYS);
    return LEAVE_SUMMARY_HEADERS.filter((header) => keys.has(header.key));
  }, []);

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === LEAVE_SUMMARY_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== LEAVE_SUMMARY_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: LEAVE_SUMMARY_DEFAULT_VIEW_ID,
        name: "Default View",
        headerConfig: baseDefaultHeaders.map((header) => ({
          key: header.key,
          name: header.name,
        })),
        headerKeys: baseDefaultHeaders.map((header) => header.key),
        isDefaultView: true,
      },
    [baseDefaultHeaders, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === LEAVE_SUMMARY_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      LEAVE_SUMMARY_HEADERS.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = LEAVE_SUMMARY_HEADERS.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders]);

  const handleLeaveSummaryDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const sumField = (key) =>
        rows.reduce((sum, row) => sum + safeNumber(row?.[key]), 0);
      const summaryRow = {
        rowType: "grand_total",
        user_name: "TOTAL",
        annual_allowance: sumField("annual_allowance"),
        credited_leave: sumField("credited_leave"),
        planned_leave: sumField("planned_leave"),
        used_leave: sumField("used_leave"),
        expired_leave: sumField("expired_leave"),
        available_leave: sumField("available_leave"),
      };

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Employee Leave Balances",
        {},
        // reportMetadata is memoized off filter/summary state, not time, so
        // it goes stale between page load and the moment the user actually
        // clicks Download — stamp the real download time here instead.
        {
          ...reportMetadata,
          "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
        },
        [],
      );

      if (typeof onProgress === "function") onProgress(100);
    },
    [downloadCsv, downloadExcel, downloadPdf, reportMetadata, visibleHeaders],
  );

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(LEAVE_SUMMARY_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === LEAVE_SUMMARY_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(LEAVE_SUMMARY_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(LEAVE_SUMMARY_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || LEAVE_SUMMARY_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(LEAVE_SUMMARY_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = (
    <SavedViewSelector
      headers={LEAVE_SUMMARY_HEADERS}
      defaultHeaders={baseDefaultHeaders}
      defaultView={defaultView}
      savedViews={customSavedViews}
      activeView={activeView}
      activeViewId={effectiveViewId}
      persistedDefaultViewId={persistedDefaultViewId}
      screenAccess={viewer_access}
      onSelectDefaultView={handleSelectDefaultView}
      onSelectView={handleSelectView}
      onCreateView={handleCreateView}
      onUpdateView={(viewId, viewName, headerConfig) => {
        if (viewId === LEAVE_SUMMARY_DEFAULT_VIEW_ID) {
          handleEditDefaultView(viewId, viewName, headerConfig);
          return;
        }
        updateView(viewId, viewName, headerConfig);
      }}
      onDeleteView={handleDeleteView}
      onMakeDefaultView={handleMakeDefaultView}
    />
  );

  return (
    <ThemeProvider theme={designTheme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Mui.Box sx={{ ...designShell.pageSurface, px: 3, py: 3 }}>
          <Mui.Stack spacing={2.5}>
            {loading ? (
              <LeaveSummarySkeleton />
            ) : (
              <>
                <Mui.Stack
                  direction={{ xs: "column", lg: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", lg: "center" }}
                  spacing={2}
                >
                  <Mui.Box>
                    <GlobalBreadcrumb
                      trail={[
                        {
                          label: "Team Workspace",
                          onClick: () => history.push("/admin/teamzone/home"),
                        },
                        "Leave Balances",
                      ]}
                    />
                    <Mui.Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Mui.Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${designColors.tealTint} 0%, #F4FBF8 100%)`,
                          color: designColors.tealDark,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <AccountBalanceWallet sx={{ fontSize: 30 }} />
                      </Mui.Box>
                      <Mui.Box>
                        <Mui.Typography sx={designType.pageTitle}>
                          Leave Balances
                        </Mui.Typography>
                        <Mui.Typography sx={designType.pageSubtitle}>
                          View leave balances, usage trends and manage leave
                          entitlements.
                        </Mui.Typography>
                      </Mui.Box>
                    </Mui.Stack>
                  </Mui.Box>
                  <Mui.Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: { xs: "100%", lg: "auto" },
                    }}
                  >
                    <Mui.Box
                      sx={{
                        flex: { xs: "1 1 100%", lg: "0 0 250px" },
                        minWidth: 0,
                      }}
                    >
                      <YearHeaderFilter
                        options={leaveDate}
                        selected={leaveDate.find(
                          (option) => option?.filteredValue === filteredValue,
                        )}
                        onSelect={(value) => {
                          setFilteredLabel(value.label);
                          setFilteredValue(value.filteredValue);
                          setStartDate(formatDate(value.start_date));
                          setEndDate(formatDate(value.end_date));
                          getTeammateDetails(
                            formatDate(value.start_date),
                            formatDate(value.end_date),
                            departmentId,
                          );
                        }}
                      />
                    </Mui.Box>
                  </Mui.Box>
                </Mui.Stack>

                <Mui.Box
                  sx={{
                    display: "grid",
                    // 2 columns on phones, 3 on tablets/laptops, all 5 in one
                    // row only once there is room (auto-fit squeezed them
                    // into ~200px tiles on ordinary laptop widths).
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      xl: "repeat(5, minmax(0, 1fr))",
                    },
                    "@media (max-width:599.95px)": {
                      "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
                    },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <StatCard
                    icon={Groups2}
                    title="Total Employees"
                    value={String(summary.totalEmployees)}
                    detail="In current filters"
                    accent="#16A34A"
                    tint="#EAFBF1"
                  />
                  <StatCard
                    icon={InfoOutlined}
                    title="Average Balance"
                    value={formatCount(summary.averageBalance)}
                    detail="Days · Per employee"
                    accent="#2563EB"
                    tint="#EFF6FF"
                  />
                  <StatCard
                    icon={AccountBalanceWallet}
                    title="Total Available"
                    value={formatCount(summary.totalAvailable)}
                    detail="Days · Across the team"
                    accent={designColors.tealDark}
                    tint={designColors.tealTint}
                  />
                  <StatCard
                    icon={WarningAmberRounded}
                    title="Expiring This Period"
                    value={formatCount(summary.totalExpired)}
                    detail="Days · Review before cutoff"
                    accent="#F97316"
                    tint="#FFF7ED"
                  />
                  <StatCard
                    icon={WarningAmberRounded}
                    title="Low Balance"
                    value={String(summary.lowBalanceEmployees)}
                    detail="Employees ≤ 3 days"
                    accent="#EF4444"
                    tint="#FEF2F2"
                  />
                </Mui.Box>

                <Mui.Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      lg: upcomingLeave.preview.length
                        ? "repeat(3, minmax(0, 1fr))"
                        : "repeat(2, minmax(0, 1fr))",
                    },
                    "@media (min-width:900px) and (max-width:1199.95px)": {
                      "& > :last-child:nth-child(odd)": {
                        gridColumn: "1 / -1",
                      },
                    },
                  }}
                >
                  <Mui.Paper
                    elevation={0}
                    sx={{
                      ...designShell.sectionCard,
                      p: { xs: 1.75, sm: 2.25 },
                      minHeight: 315,
                      minWidth: 0,
                      // Donut/legend layout follows this card's own width,
                      // not the window (3 columns squeeze it on laptops).
                      containerType: "inline-size",
                    }}
                  >
                    <SectionHeader
                      title="Leave Balance Distribution"
                      tooltip="How the team's remaining leave balance is spread across five ranges, based on each employee's available days in the selected leave year."
                    />
                    <Mui.Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        pt: 2,
                        gap: 3,
                        "@container (max-width: 440px)": {
                          flexDirection: "column",
                          alignItems: "stretch",
                          gap: 2,
                        },
                      }}
                    >
                      <Mui.Box
                        sx={{
                          width: 170,
                          height: 170,
                          borderRadius: "50%",
                          background: summary.distributionBackground,
                          position: "relative",
                          "@container (max-width: 440px)": { mx: "auto" },
                          flexShrink: 0,
                        }}
                      >
                        <Mui.Box
                          sx={{
                            position: "absolute",
                            inset: 35,
                            borderRadius: "50%",
                            backgroundColor: designColors.white,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Mui.Typography
                            sx={{
                              fontSize: 28,
                              fontWeight: 700,
                              color: designColors.slate900,
                            }}
                          >
                            {summary.totalEmployees}
                          </Mui.Typography>
                          <Mui.Typography
                            sx={{ fontSize: 13, color: designColors.slate600 }}
                          >
                            Employees
                          </Mui.Typography>
                        </Mui.Box>
                      </Mui.Box>
                      <Mui.Stack spacing={1.2} sx={{ flex: 1, minWidth: 0 }}>
                        {summary.distribution.map((item) => {
                          const pct = summary.totalEmployees
                            ? Math.round(
                                (item.count / summary.totalEmployees) * 100,
                              )
                            : 0;
                          return (
                            <Mui.Stack
                              key={item.label}
                              direction="row"
                              justifyContent="space-between"
                              spacing={1}
                              sx={{ minWidth: 0 }}
                            >
                              <Mui.Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ minWidth: 0 }}
                              >
                                <Mui.Box
                                  sx={{
                                    width: 11,
                                    height: 11,
                                    borderRadius: "50%",
                                    backgroundColor: item.color,
                                    flexShrink: 0,
                                  }}
                                />
                                <Mui.Typography
                                  sx={{
                                    fontSize: 13.5,
                                    color: designColors.slate700,
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {item.label}
                                </Mui.Typography>
                              </Mui.Stack>
                              <Mui.Typography
                                sx={{
                                  fontSize: 13.5,
                                  fontWeight: 600,
                                  color: designColors.slate900,
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              >
                                {item.count} ({pct}%)
                              </Mui.Typography>
                            </Mui.Stack>
                          );
                        })}
                      </Mui.Stack>
                    </Mui.Stack>
                  </Mui.Paper>
                  <Mui.Paper
                    elevation={0}
                    sx={{
                      ...designShell.sectionCard,
                      p: { xs: 1.75, sm: 2.25 },
                      minHeight: 315,
                      minWidth: 0,
                    }}
                  >
                    <SectionHeader
                      title="Employees Requiring Attention"
                      tooltip="Employees with 3 or fewer leave days remaining, or leave that expires within the selected leave year, so you can follow up before it lapses."
                    />
                    <Mui.Stack spacing={1.5} sx={{ pt: 2 }}>
                      {attentionSections.map((section) => {
                        const visibleItems = section.items.slice(
                          0,
                          ATTENTION_PREVIEW_COUNT,
                        );
                        const hasMore =
                          section.items.length > ATTENTION_PREVIEW_COUNT;
                        return (
                          <Mui.Box
                            key={section.key}
                            sx={{
                              p: 1.25,
                              borderRadius: 2,
                              backgroundColor: section.tint,
                            }}
                          >
                            <Mui.Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ flexWrap: "wrap", rowGap: 0.75, columnGap: 1 }}
                            >
                              <Mui.Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ minWidth: 0 }}
                              >
                                <section.icon
                                  sx={{ fontSize: 18, color: section.accent }}
                                />
                                <Mui.Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: designColors.slate800,
                                  }}
                                >
                                  {section.title}
                                </Mui.Typography>
                              </Mui.Stack>
                              <Mui.Chip
                                label={`${section.count} Employee${section.count === 1 ? "" : "s"}`}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: section.accent,
                                  backgroundColor: designColors.white,
                                }}
                              />
                            </Mui.Stack>
                            <Mui.Stack spacing={0.4} sx={{ mt: 1 }}>
                              {visibleItems.length ? (
                                visibleItems.map((item) => (
                                  <Mui.Stack
                                    key={item.name}
                                    direction="row"
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{ minWidth: 0 }}
                                  >
                                    <Mui.Typography
                                      sx={{
                                        fontSize: 12.5,
                                        color: designColors.slate700,
                                        minWidth: 0,
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {item.name}
                                    </Mui.Typography>
                                    <Mui.Typography
                                      sx={{
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        color: section.accent,
                                        flexShrink: 0,
                                        textAlign: "right",
                                      }}
                                    >
                                      {item.detail}
                                    </Mui.Typography>
                                  </Mui.Stack>
                                ))
                              ) : (
                                <Mui.Typography
                                  sx={{
                                    fontSize: 12.5,
                                    color: designColors.slate500,
                                  }}
                                >
                                  None right now.
                                </Mui.Typography>
                              )}
                            </Mui.Stack>
                            {hasMore ? (
                              <Mui.Button
                                onClick={() => handleViewAllAttention(section)}
                                sx={{
                                  minWidth: 0,
                                  p: 0,
                                  mt: 0.75,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: 12.5,
                                }}
                              >
                                View all
                              </Mui.Button>
                            ) : null}
                          </Mui.Box>
                        );
                      })}
                    </Mui.Stack>
                  </Mui.Paper>
                  {upcomingLeave.preview.length ? (
                    <Mui.Paper
                      elevation={0}
                      sx={{
                        ...designShell.sectionCard,
                        p: { xs: 1.75, sm: 2.25 },
                        minHeight: 315,
                        minWidth: 0,
                      }}
                    >
                      <SectionHeader
                        title="Upcoming Leave"
                        tooltip="The soonest approved leave requests coming up for your team, with each request's leave type, dates and duration."
                        actionLabel={
                          upcomingLeave.all.length > ATTENTION_PREVIEW_COUNT
                            ? "View all"
                            : undefined
                        }
                        onAction={handleViewAllUpcoming}
                      />
                      <Mui.Stack spacing={1.1} sx={{ pt: 2 }}>
                        {upcomingLeave.preview.map((item, index) => (
                          <Mui.Stack
                            key={`${item.name}-${item.fromDate}-${index}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1.25}
                          >
                            <Mui.Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Mui.Avatar
                                src={item.imageUrl ?? optionalAvathar}
                                alt={item.name?.charAt(0)?.toUpperCase()}
                                sx={{ width: 36, height: 36 }}
                              />
                              <Mui.Box sx={{ minWidth: 0 }}>
                                <Mui.Typography
                                  sx={{
                                    fontSize: 13.5,
                                    fontWeight: 600,
                                    color: designColors.slate900,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.name}
                                </Mui.Typography>
                                <Mui.Typography
                                  sx={{
                                    fontSize: 12.5,
                                    color: designColors.slate600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: { xs: "normal", sm: "nowrap" },
                                  }}
                                >
                                  {item.leaveType} ·{" "}
                                  {item.fromDate === item.toDate
                                    ? item.fromDate
                                    : `${item.fromDate} – ${item.toDate}`}
                                </Mui.Typography>
                              </Mui.Box>
                            </Mui.Stack>
                            <Mui.Stack
                              sx={{
                                minWidth: 60,
                                px: 1,
                                py: 0.55,
                                borderRadius: 2,
                                backgroundColor: designColors.tealTint,
                                textAlign: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Mui.Typography
                                sx={{
                                  fontSize: 15,
                                  fontWeight: 700,
                                  color: designColors.tealDark,
                                  lineHeight: 1,
                                }}
                              >
                                {formatCount(item.days)}
                              </Mui.Typography>
                              <Mui.Typography
                                sx={{
                                  fontSize: 11,
                                  color: designColors.slate600,
                                }}
                              >
                                Days
                              </Mui.Typography>
                            </Mui.Stack>
                          </Mui.Stack>
                        ))}
                      </Mui.Stack>
                    </Mui.Paper>
                  ) : null}
                </Mui.Box>

                {/* Page-level sticky table chrome (not a boxed internal scroll
                area) — same pattern as Approve/Leave's list
                (TeamZone/Approve/Leave/index.js): the CustomFilter bar sticks
                under the app bar, the table head sticks under that (offset by
                the filter bar's measured height), and the footer sticks to
                the bottom, all pinned against the real page scroll. Requires
                every ancestor here to stay overflow:"visible" — an
                overflow:"hidden" card (like designShell.tableCard) would clip
                the sticky positioning instead of letting it pin in place. */}
                <Mui.Box
                  ref={customFilterRef}
                  sx={approveStickyCustomFilterBar}
                >
                  <CustomFilter
                    title="Employee Leave Balances"
                    titleOverride={titleOverride}
                    filterHeaders={visibleHeaders}
                    data={masterDataForSearch}
                    filteredData={leaveFilterData}
                    onFilteredDataChange={handleFilteredDataChange}
                    screenAccess={viewer_access}
                    hideCount
                    downloadFormats={["excel", "pdf", "csv"]}
                    includeMetadata
                    metadata={reportMetadata}
                    customDownloadHandler={handleLeaveSummaryDownload}
                    labeledToolbar
                    searchPlaceholder="Search by name, employee ID, department..."
                  />
                </Mui.Box>
                <Mui.Box sx={{ ...designShell.sectionCard, borderRadius: 0 }}>
                  {loading ? (
                    <Mui.Box sx={{ py: 8 }}>
                      <Loader info="Loading..." />
                    </Mui.Box>
                  ) : (
                    <Mui.TableContainer
                      ref={containerRef}
                      sx={{
                        width: "100%",
                        overflowX: { xs: "auto", sm: "visible" },
                        overflowY: { xs: "auto", sm: "visible" },
                        pb: footerHeight ? `${footerHeight}px` : 0,
                      }}
                    >
                      <ApproveTable stickyHeader sx={{ minWidth: 900, width: "100%" }}>
                        <Mui.TableHead sx={designShell.tableStickyHead}>
                          <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                            {visibleHeaders.map((header) => (
                              <Mui.TableCell
                                key={header.key}
                                sx={{
                                  ...approveTableHeadCell,
                                  top: APP_BAR_OFFSET + customFilterHeight,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {header.name}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        </Mui.TableHead>
                        <Mui.TableBody>
                          {paginatedRows.length ? (
                            paginatedRows.map((user, index) => {
                              const balance = safeNumber(user?.available_leave);
                              return (
                                <Mui.TableRow
                                  key={`${user?.employee_id || user?.user_name}-${index}`}
                                  hover
                                  onClick={() => {
                                    handleVisibilityToggle(user);
                                    dispatch(setGlobalSearchValue(""));
                                  }}
                                  sx={{
                                    cursor: "pointer",
                                    ...designShell.tableRowHover,
                                  }}
                                >
                                  {visibleHeaders.map((header) => (
                                    <Mui.TableCell
                                      key={header.key}
                                      data-label={header.name}
                                      sx={approveTableBodyCell}
                                    >
                                      {header.key === "user_name" ? (
                                        <Mui.Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          <Mui.Avatar
                                            sx={{ width: 28, height: 28 }}
                                            alt={user?.user_name
                                              ?.charAt(0)
                                              ?.toUpperCase()}
                                            src={
                                              user?.image_url ?? optionalAvathar
                                            }
                                          />
                                          <Mui.Typography
                                            sx={{
                                              fontWeight: 500,
                                              color: designColors.slate900,
                                            }}
                                          >
                                            {user?.user_name || "N/A"}
                                          </Mui.Typography>
                                        </Mui.Stack>
                                      ) : header.key === "available_leave" ? (
                                        <Mui.Chip
                                          label={formatCount(balance)}
                                          size="small"
                                          sx={{
                                            fontWeight: 600,
                                            ...getBalanceChipSx(balance),
                                          }}
                                        />
                                      ) : header.key === "department" ||
                                        header.key === "employee_id" ? (
                                        user?.[header.key] || "N/A"
                                      ) : (
                                        formatCount(
                                          safeNumber(user?.[header.key]),
                                        )
                                      )}
                                    </Mui.TableCell>
                                  ))}
                                </Mui.TableRow>
                              );
                            })
                          ) : (
                            <TableEmptyState
                              dataLength={masterDataForSearch}
                              colSpan={visibleHeaders.length}
                            />
                          )}
                        </Mui.TableBody>
                      </ApproveTable>
                    </Mui.TableContainer>
                  )}

                  {!loading && leaveFilterData.length > 0 && (
                    <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
                      {paginationFooter({
                        page,
                        rowsPerPage,
                        total: leaveFilterData.length,
                        itemLabel: "employees",
                        onPageChange: setPage,
                        onRowsPerPageChange: handleChangeRowsPerPage,
                        rowsPerPageOptions,
                      })}
                    </Mui.Box>
                  )}
                </Mui.Box>
              </>
            )}
          </Mui.Stack>
        </Mui.Box>

        <LeaveBalanceDetailPanel
          open={showDetails}
          onClose={handleClose}
          selectedUser={selectedUser}
          expanded={leaveDetailPanelExpanded}
          onToggleExpand={toggleLeaveDetailPanelExpanded}
          checkAccess={check_access}
          onDataChanged={() => setUpdateData(true)}
        />

        <ViewAllListPanel
          panel={viewAllPanel}
          expanded={viewAllPanelExpanded}
          onToggleExpand={toggleViewAllPanelExpanded}
          onClose={handleCloseViewAllPanel}
        />
      </FilterProvider>
    </ThemeProvider>
  );
};
